/*
  Lightweight carousel that replaces Flickity. It uses the same physics,
  wrap-around and drag behavior as Flickity v2, without the library.

  Markup:
    <carousel-slider data-wrap>
      <div class="carousel-slider__viewport" data-carousel-viewport>
        <div class="carousel-slider__track" data-carousel-track> ...cells... </div>
      </div>
      <button type="button" class="carousel-slider__button carousel-slider__button--prev" data-carousel-prev></button>
      <button type="button" class="carousel-slider__button carousel-slider__button--next" data-carousel-next></button>
      <div class="carousel-slider__dots" data-carousel-dots></div>
    </carousel-slider>

  Options (attributes), with their Flickity equivalents:
    data-wrap                   wrapAround: true
    data-contain                contain: true
    data-cell-align="center"    cellAlign ("left" by default)
    data-autoplay="3000"        autoPlay: 3000
    data-pause-on-hover="false" pauseAutoPlayOnHover: false
    data-keep-playing           Autoplay resumes after the visitor interacts
                                (Flickity stops it for good).
    disabled                    Turns the carousel off and leaves the cells in
                                their normal layout (like flickity.destroy()).
                                Remove the attribute to turn it back on.

  Methods: select(index), next(), previous(), refresh().
  Events: "carousel:change" with detail.index when the selected cell changes.

  When a wrapping carousel's cells don't cover the viewport plus one cell,
  hidden copies (data-carousel-clone) are added to the track so the loop has
  no gaps. Indexes in select() and carousel:change refer to the originals.

  Classes set on the element: is-draggable, is-dragging, and is-static when a
  non-wrapping carousel's cells all fit without sliding.
*/
if (!customElements.get('carousel-slider')) {
  // Flickity v2 defaults
  const FRICTION = 0.28;
  const SELECTED_ATTRACTION = 0.025;
  const DRAG_THRESHOLD = 3;

  const modulo = (number, divisor) => ((number % divisor) + divisor) % divisor;

  class CarouselSlider extends HTMLElement {
    static get observedAttributes() {
      return ['data-wrap', 'data-contain', 'data-cell-align', 'disabled'];
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.viewport || !this.track) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');

      this.x = 0;
      this.velocity = 0;
      this.selectedIndex = 0;
      this.cells = [];
      this.isAnimating = false;
      this.isPointerDown = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.readOptions();

      this.animate = this.animate.bind(this);
      this.onPrevClick = () => {
        this.uiChange();
        this.previous();
      };
      this.onNextClick = () => {
        this.uiChange();
        this.next();
      };
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = (event) => event.preventDefault();

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('click', this.onClickCapture, true);
      this.viewport.addEventListener('dragstart', this.onDragStart);

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);

      this.refresh();
      this.setUpAutoplay();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.resizeObserver.disconnect();
      cancelAnimationFrame(this.animationFrame);
      this.isAnimating = false;
      this.stopAutoplay();
      this.removeWindowListeners();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('click', this.onClickCapture, true);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
    }

    attributeChangedCallback() {
      if (!this.track) return;
      this.readOptions();
      this.refresh();
    }

    readOptions() {
      const alignments = { left: 0, center: 0.5, right: 1 };
      this.options = {
        wrap: this.hasAttribute('data-wrap'),
        contain: this.hasAttribute('data-contain'),
        cellAlign: alignments[this.dataset.cellAlign] ?? 0,
      };
    }

    /* ---------- Layout ---------- */

    // Measures every cell and works out where the track stops for each one.
    refresh() {
      if (this.hasAttribute('disabled')) {
        this.deactivate();
        return;
      }

      // Like Flickity, the carousel is focusable and responds to arrow keys.
      if (!this.hasAttribute('tabindex')) {
        this.tabIndex = 0;
        this.addedTabIndex = true;
      }

      const align = this.options.cellAlign;
      this.viewportWidth = this.viewport.clientWidth;
      this.cursorPosition = this.viewportWidth * align;

      this.removeClones();
      this.originalCount = this.track.children.length;
      this.measureCells();

      // Like Flickity, a wrapping carousel keeps sliding even when every cell
      // fits. Only a non-wrapping one goes static.
      this.isWrapping = this.options.wrap && this.originalCount > 1;

      // Flickity shows each cell once, so when one set of cells is narrower
      // than the viewport plus a cell, a cell would have to be at both ends at
      // once and a gap opens while looping. Copies of the set close the gap.
      if (this.isWrapping) {
        const widestCell = Math.max(...this.cells.map((cell) => cell.outerWidth));
        const setsNeeded = Math.ceil((this.viewportWidth + widestCell) / this.slideableWidth);
        if (setsNeeded > 1) {
          this.addClones(setsNeeded - 1);
          this.measureCells();
        }
      }

      const count = this.cells.length;
      const lastCell = this.cells[count - 1];
      const contentWidth = lastCell ? this.slideableWidth - lastCell.marginRight : 0;
      const isStatic = !this.isWrapping && contentWidth <= this.viewportWidth + 1;

      this.isDraggable = count > 1 && !isStatic;
      this.classList.toggle('is-static', isStatic);
      this.classList.toggle('is-draggable', this.isDraggable);
      this.classList.toggle('is-wrapping', this.isWrapping);

      if (isStatic) {
        this.cells.forEach((cell) => (cell.target = contentWidth * align));
      } else if (this.options.contain && !this.isWrapping) {
        const beginBound = this.cursorPosition + this.cells[0].marginLeft;
        const endBound = contentWidth - this.viewportWidth * (1 - align);
        this.cells.forEach((cell) => {
          cell.target = Math.min(Math.max(cell.target, beginBound), endBound);
        });
      }

      if (this.isWrapping) {
        this.beforeShiftCells = this.getGapCells(this.cursorPosition, count - 1, -1);
        this.afterShiftCells = this.getGapCells(this.viewportWidth - this.cursorPosition, 0, 1);
      } else {
        this.beforeShiftCells = [];
        this.afterShiftCells = [];
      }

      this.selectedIndex = Math.min(this.selectedIndex, Math.max(count - 1, 0));
      this.renderDots();
      this.updateSelected();
      this.positionSliderAtSelected();
    }

    measureCells() {
      const align = this.options.cellAlign;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      let cellX = 0;

      this.cells = Array.from(this.track.children).map((element) => {
        element.style.translate = '';
        const style = getComputedStyle(element);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = (parseFloat(style.marginRight) || 0) + gap;
        const width = element.getBoundingClientRect().width;
        const cell = {
          element,
          x: cellX,
          width,
          marginLeft,
          marginRight,
          outerWidth: marginLeft + width + marginRight,
          target: cellX + marginLeft + width * align,
          shift: 0,
        };
        cellX += cell.outerWidth;
        return cell;
      });

      this.slideableWidth = cellX;
    }

    // Copies are hidden from screen readers and skipped by the Tab key, so
    // only the original cells are announced and focusable.
    addClones(sets) {
      const originals = Array.from(this.track.children);
      for (let set = 0; set < sets; set++) {
        originals.forEach((element) => {
          const clone = element.cloneNode(true);
          clone.setAttribute('data-carousel-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.classList.remove('is-selected');
          // Same files as the originals, so load them now rather than as
          // they slide into view.
          clone.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
          [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')].forEach((el) => {
            el.removeAttribute('id');
            el.removeAttribute('data-shopify-editor-block');
          });
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
          this.track.appendChild(clone);
        });
      }
    }

    removeClones() {
      this.track.querySelectorAll(':scope > [data-carousel-clone]').forEach((clone) => clone.remove());
    }

    // Puts the cells back in their normal layout, like flickity.destroy().
    deactivate() {
      this.removeClones();
      cancelAnimationFrame(this.animationFrame);
      this.isAnimating = false;
      this.isDraggable = false;
      this.isWrapping = false;
      this.selectedIndex = 0;
      this.cells = [];
      this.track.style.transform = '';
      Array.from(this.track.children).forEach((element) => {
        element.style.translate = '';
        element.inert = false;
        element.classList.remove('is-selected');
      });
      this.classList.remove('is-draggable', 'is-dragging', 'is-static', 'is-wrapping');
      this.dotsContainer?.replaceChildren();

      if (this.addedTabIndex) {
        this.removeAttribute('tabindex');
        this.addedTabIndex = false;
      }
    }

    // Cells that have to jump to the other end to fill a gap while wrapping.
    getGapCells(gapX, cellIndex, increment) {
      const cells = [];
      while (gapX > 0) {
        const cell = this.cells[cellIndex];
        if (!cell) break;
        cells.push(cell);
        cellIndex += increment;
        gapX -= cell.outerWidth;
      }
      return cells;
    }

    getRenderX() {
      return this.isWrapping ? modulo(this.x, this.slideableWidth) - this.slideableWidth : this.x;
    }

    positionSlider() {
      const x = this.getRenderX();
      if (this.isWrapping) this.shiftWrapCells(x);

      // The track stays on its own GPU layer (see the CSS), so moving it never
      // repaints. At rest it sits on a whole pixel to keep text crisp.
      const value = x + this.cursorPosition;
      this.track.style.transform = `translateX(${this.isAnimating ? value : Math.round(value)}px)`;
    }

    positionSliderAtSelected() {
      const cell = this.cells[this.selectedIndex];
      if (!cell) return;
      this.x = -cell.target;
      this.velocity = 0;
      this.positionSlider();
      this.updateVisibility();
    }

    shiftWrapCells(x) {
      const beforeGap = this.cursorPosition + x;
      this.shiftCells(this.beforeShiftCells, beforeGap, -1);
      const afterGap = this.viewportWidth - (x + this.slideableWidth + this.cursorPosition);
      this.shiftCells(this.afterShiftCells, afterGap, 1);
    }

    shiftCells(cells, gap, shift) {
      cells.forEach((cell) => {
        const cellShift = gap > 0 ? shift : 0;
        if (cell.shift !== cellShift) {
          cell.shift = cellShift;
          cell.element.style.translate = cellShift ? `${cellShift * this.slideableWidth}px` : '';
        }
        gap -= cell.outerWidth;
      });
    }

    // Cells fully outside the viewport are made inert so keyboard and screen
    // reader users don't land on content they can't see.
    updateVisibility() {
      const translate = this.getRenderX() + this.cursorPosition;
      this.cells.forEach((cell) => {
        const left = translate + cell.x + cell.marginLeft + cell.shift * this.slideableWidth;
        cell.element.inert = left + cell.width <= 1 || left >= this.viewportWidth - 1;
      });
    }

    /* ---------- Selection ---------- */

    // Selects one of the original cells (copies made for looping don't
    // count), taking the shortest way there.
    select(index, isWrap = false, isInstant = false) {
      index = parseInt(index, 10);
      const originals = this.originalCount;
      if (!originals || index < 0 || index >= originals) return;

      const setStart = this.selectedIndex - (this.selectedIndex % originals);
      const closest = [index - originals, index, index + originals].map((candidate) => setStart + candidate);
      closest.sort((a, b) => Math.abs(a - this.selectedIndex) - Math.abs(b - this.selectedIndex));
      this.selectCell(closest[0], isWrap, isInstant);
    }

    selectCell(index, isWrap = false, isInstant = false) {
      const count = this.cells.length;
      if (!count) return;

      this.wrapSelect(index);
      if (this.isWrapping || isWrap) index = modulo(index, count);
      if (!this.cells[index]) return;

      const hasChanged = index !== this.selectedIndex;
      this.selectedIndex = index;
      this.updateSelected();

      if (isInstant) {
        this.positionSliderAtSelected();
      } else {
        this.startAnimation();
      }

      if (hasChanged) {
        this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: index % this.originalCount } }));
      }
    }

    next(isWrap) {
      this.selectCell(this.selectedIndex + 1, isWrap);
    }

    previous(isWrap) {
      this.selectCell(this.selectedIndex - 1, isWrap);
    }

    // When wrapping, travel the short way around and keep x in range.
    wrapSelect(index) {
      if (!this.isWrapping) return;
      const count = this.cells.length;
      const wrapIndex = modulo(index, count);
      const delta = Math.abs(wrapIndex - this.selectedIndex);
      const backWrapDelta = Math.abs(wrapIndex + count - this.selectedIndex);
      const forwardWrapDelta = Math.abs(wrapIndex - count - this.selectedIndex);

      if (!this.isDragSelect && backWrapDelta < delta) {
        index += count;
      } else if (!this.isDragSelect && forwardWrapDelta < delta) {
        index -= count;
      }

      if (index < 0) {
        this.x -= this.slideableWidth;
      } else if (index >= count) {
        this.x += this.slideableWidth;
      }
    }

    updateSelected() {
      this.cells.forEach((cell, index) => cell.element.classList.toggle('is-selected', index === this.selectedIndex));

      const selectedOriginal = this.selectedIndex % this.originalCount;
      Array.from(this.dotsContainer?.children || []).forEach((dot, index) => {
        dot.classList.toggle('is-selected', index === selectedOriginal);
        dot.setAttribute('aria-current', index === selectedOriginal ? 'true' : 'false');
      });

      const lastIndex = this.cells.length - 1;
      if (this.prevButton) this.prevButton.disabled = !this.isWrapping && this.selectedIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = !this.isWrapping && this.selectedIndex >= lastIndex;
    }

    renderDots() {
      if (!this.dotsContainer || this.dotsContainer.children.length === this.originalCount) return;

      this.dotsContainer.replaceChildren(
        ...this.cells.slice(0, this.originalCount).map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel-slider__dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => {
            this.uiChange();
            this.select(index);
          });
          return dot;
        })
      );
    }

    onKeydown(event) {
      if (event.target !== this) return;
      if (event.key === 'ArrowLeft') {
        this.uiChange();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        this.uiChange();
        this.next();
      }
    }

    // Visitor used the arrows, dots or keyboard.
    uiChange() {
      if (!this.keepPlaying) this.stopAutoplay();
    }

    /* ---------- Animation ---------- */

    startAnimation() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.restingFrames = 0;
      this.animate();
    }

    animate() {
      this.applyDragForce();
      this.applySelectedAttraction();

      const previousX = this.x;
      this.x += this.velocity;
      this.velocity *= 1 - FRICTION;
      this.positionSlider();
      this.settle(previousX);

      if (this.isAnimating) this.animationFrame = requestAnimationFrame(this.animate);
    }

    settle(previousX) {
      const isResting = !this.isPointerDown && Math.round(this.x * 100) === Math.round(previousX * 100);
      if (isResting) this.restingFrames++;
      if (this.restingFrames > 2) {
        this.isAnimating = false;
        this.positionSlider();
        this.updateVisibility();
      }
    }

    applySelectedAttraction() {
      if (this.isDraggable && this.isPointerDown) return;
      const cell = this.cells[this.selectedIndex];
      if (!cell) return;
      this.velocity += (-cell.target - this.x) * SELECTED_ATTRACTION;
    }

    applyDragForce() {
      if (!this.isDraggable || !this.isPointerDown) return;
      const dragVelocity = this.dragX - this.x;
      this.velocity += dragVelocity - this.velocity;
    }

    /* ---------- Dragging ---------- */

    onPointerDown(event) {
      if (!this.isDraggable || this.isPointerDown) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('input, textarea, select, [contenteditable]')) return;

      if (event.pointerType === 'mouse') {
        event.preventDefault();
        this.focus({ preventScroll: true });
      }

      this.isPointerDown = true;
      this.isDragging = false;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.dragStartPosition = this.x;
      this.dragX = this.x;
      this.previousDragX = undefined;

      if (this.keepPlaying) {
        this.pauseAutoplay();
      } else {
        this.stopAutoplay();
      }

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      this.startAnimation();
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      const moveX = event.clientX - this.pointerStartX;

      if (!this.isDragging) {
        if (Math.abs(moveX) <= DRAG_THRESHOLD) return;
        this.isDragging = true;
        this.isPreventingClicks = true;
        this.classList.add('is-dragging');
      }

      let dragX = this.dragStartPosition + moveX;

      // Resist dragging past the first and last cell.
      if (!this.isWrapping && this.cells.length) {
        const originBound = Math.max(-this.cells[0].target, this.dragStartPosition);
        if (dragX > originBound) dragX = (dragX + originBound) * 0.5;
        const endBound = Math.min(-this.cells[this.cells.length - 1].target, this.dragStartPosition);
        if (dragX < endBound) dragX = (dragX + endBound) * 0.5;
      }

      this.previousDragX = this.dragX;
      this.dragX = dragX;
      this.dragMoveTime = Date.now();
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      this.removeWindowListeners();
      this.isPointerDown = false;

      if (this.isDragging) {
        this.isDragging = false;
        this.classList.remove('is-dragging');
        this.dragEnd();
        setTimeout(() => (this.isPreventingClicks = false));
      }

      if (this.keepPlaying) this.unpauseAutoplay();
    }

    removeWindowListeners() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    // Swallow the click that ends a drag so links inside cells don't fire.
    onClickCapture(event) {
      if (!this.isPreventingClicks) return;
      event.preventDefault();
      event.stopPropagation();
    }

    dragEnd() {
      let index = this.dragEndRestingSelect();
      if (index === this.selectedIndex) index += this.dragEndBoostSelect();

      this.previousDragX = undefined;
      this.isDragSelect = this.isWrapping;
      this.selectCell(index);
      this.isDragSelect = false;
    }

    // Picks the cell closest to where the slider would coast to a stop.
    dragEndRestingSelect() {
      const restingX = this.x + this.velocity / FRICTION;
      const distance = Math.abs(this.getCellDistance(-restingX, this.selectedIndex));
      const positiveResting = this.getClosestResting(restingX, distance, 1);
      const negativeResting = this.getClosestResting(restingX, distance, -1);
      return positiveResting.distance < negativeResting.distance ? positiveResting.index : negativeResting.index;
    }

    getClosestResting(restingX, distance, increment) {
      const allowEqual = this.options.contain && !this.isWrapping;
      let index = this.selectedIndex;
      let minDistance = Infinity;

      while (allowEqual ? distance <= minDistance : distance < minDistance) {
        index += increment;
        minDistance = distance;
        distance = this.getCellDistance(-restingX, index);
        if (distance === null) break;
        distance = Math.abs(distance);
      }

      return { distance: minDistance, index: index - increment };
    }

    getCellDistance(x, index) {
      const count = this.cells.length;
      const cell = this.cells[this.isWrapping ? modulo(index, count) : index];
      if (!cell) return null;
      const wrap = this.isWrapping ? this.slideableWidth * Math.floor(index / count) : 0;
      return x - (cell.target + wrap);
    }

    // A quick flick moves at least one cell even if it was a short drag.
    dragEndBoostSelect() {
      if (this.previousDragX === undefined || !this.dragMoveTime || Date.now() - this.dragMoveTime > 100) return 0;
      const distance = this.getCellDistance(-this.dragX, this.selectedIndex);
      const delta = this.previousDragX - this.dragX;
      if (distance > 0 && delta > 0) return 1;
      if (distance < 0 && delta < 0) return -1;
      return 0;
    }

    /* ---------- Autoplay ---------- */

    setUpAutoplay() {
      this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
      this.keepPlaying = this.hasAttribute('data-keep-playing');
      this.autoplayState = 'stopped';
      if (!this.autoplayDelay || this.reducedMotion) return;

      if (this.dataset.pauseOnHover !== 'false') {
        this.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.addEventListener('mouseleave', () => this.unpauseAutoplay());
      }

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.unpauseAutoplay();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.playAutoplay();
    }

    playAutoplay() {
      if (!this.autoplayDelay || this.reducedMotion) return;
      this.autoplayState = 'playing';
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = setTimeout(() => {
        if (this.autoplayState !== 'playing') return;
        this.next(true);
        this.playAutoplay();
      }, this.autoplayDelay);
    }

    pauseAutoplay() {
      if (this.autoplayState !== 'playing') return;
      this.autoplayState = 'paused';
      clearTimeout(this.autoplayTimer);
    }

    unpauseAutoplay() {
      if (this.autoplayState === 'paused') this.playAutoplay();
    }

    stopAutoplay() {
      this.autoplayState = 'stopped';
      clearTimeout(this.autoplayTimer);
    }
  }

  customElements.define('carousel-slider', CarouselSlider);
}
